import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchJson } from '../../../helpers';
import { dashboard_get_datasources_path } from '~/routes';

const initialState = {
  datasources: {},
  // effectiveCols per widget — may differ from raw datasource cols when grouping/pivot is active.
  // Keyed by widgetId so each widget's independent group config is reflected here.
  widgetEffectiveCols: {},
  loading: false,
  error: null,
};

export const fetchDatasourcesForDashboard = createAsyncThunk(
  'datasources/fetchAll',
  async ({ dashboardId, projectId }, { rejectWithValue }) => {
    try {
      return await fetchJson(dashboard_get_datasources_path({ id: dashboardId, project_id: projectId, _options: true}));
    } catch (e) {
      return rejectWithValue(e.message);
    }
  }
);

const datasourceSlice = createSlice({
  name: 'datasources',
  initialState,
  reducers: {
    refreshSingleDatasource(state, action) {
      const { datasourceId, rows, cols } = action.payload;
      if (datasourceId == null) return;
      // A datasource just assigned to a widget (or just saved for the first time) has no entry
      // here yet — create it instead of dropping the write, otherwise the first live fetch after
      // save has nowhere to land.
      if (!state.datasources[datasourceId]) {
        state.datasources[datasourceId] = { crossFilterModels: {} };
      }
      if (rows) state.datasources[datasourceId].rows = rows;
      if (cols) state.datasources[datasourceId].cols = cols;
    },

    setCrossDatasourceFilterModel(state, action) {
      const { datasourceId, filterId, filterModel } = action.payload;
      if (!state.datasources[datasourceId]) {
        state.datasources[datasourceId] = { crossFilterModels: {} };
      } else if (!state.datasources[datasourceId].crossFilterModels) {
        state.datasources[datasourceId].crossFilterModels = {};
      }
      state.datasources[datasourceId].crossFilterModels[filterId] = filterModel;
    },

    removeCrossDatasourceFilterModel(state, action) {
      const { filterId } = action.payload;
      Object.values(state.datasources).forEach(ds => {
        if (ds.crossFilterModels) {
          delete ds.crossFilterModels[filterId];
        }
      });
    },

    // Datasource v2 datasources used on the dashboard, keyed 'v2-<id>' so filter widgets can target them
    registerDatasourcesV2(state, action) {
      action.payload.forEach(({ key, name, cols }) => {
        state.datasources[key] = {
          ...state.datasources[key],
          id: key,
          name,
          cols,
          crossFilterModels: state.datasources[key]?.crossFilterModels ?? {},
        };
      });
    },

    setWidgetEffectiveCols(state, action) {
      const { widgetId, cols } = action.payload;
      state.widgetEffectiveCols[widgetId] = cols;
    },

  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDatasourcesForDashboard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDatasourcesForDashboard.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          Object.entries(action.payload).forEach(([id, ds]) => {
            state.datasources[id] = {
              ...ds,
              crossFilterModels: state.datasources[id]?.crossFilterModels ?? {},
            };
          });
        }
      })
      .addCase(fetchDatasourcesForDashboard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  refreshSingleDatasource,
  setCrossDatasourceFilterModel,
  removeCrossDatasourceFilterModel,
  registerDatasourcesV2,
  setWidgetEffectiveCols,
} = datasourceSlice.actions;

// Stable empty-collection sentinels.
// NEVER use inline `?? []` / `?? {}` literals inside a useSelector callback — each call creates a
// new reference, so useSelector always sees a "changed" value and triggers an infinite render loop.
const EMPTY_ROWS = [];
const EMPTY_COLS = [];
const EMPTY_CROSS_FILTERS = {};

// Selectors
//
// datasourceId is the persisted DatagridPreset/report id — it doesn't exist yet for a datasource
// that's being configured for the first time and hasn't been saved. useDatasource.js writes that
// case's rows/cols onto the widget's own data_datasource.rows/cols instead (see its effect), so
// fall back to that when the centralized, datasourceId-keyed store has nothing for this widget.
export const selectDatasourceRows = (datasourceId, widgetId) => (state) =>
  state.datasources.datasources[datasourceId]?.rows
  ?? state.dashboard.widgets[widgetId]?.data_datasource?.rows
  ?? EMPTY_ROWS;

export const selectDatasourceCols = (datasourceId, widgetId) => (state) =>
  state.datasources.datasources[datasourceId]?.cols
  ?? state.dashboard.widgets[widgetId]?.data_datasource?.cols
  ?? EMPTY_COLS;

export const selectWidgetEffectiveCols = (widgetId) => (state) =>
  state.datasources.widgetEffectiveCols[widgetId] ?? EMPTY_COLS;

// Both widgetFilter and crossFilters must use AG Grid advanced filter model format.
// widgetFilter is null when no per-widget filter is set.
// Treat empty objects/arrays as no filter — they can be stored that way when the filter is cleared.
function normalizeFilterModel(model) {
  if (model == null) return null;
  if (Array.isArray(model)) return model.length > 0 ? model : null;
  if (typeof model === 'object') return Object.keys(model).length > 0 ? model : null;
  return null;
}

export const selectCombinedFilterModel = (widgetId, datasourceId) => (state) => {
  const widgetFilter = normalizeFilterModel(state.dashboard.widgets[widgetId]?.data_datasource?.filter_model);
  const crossFilterModels = state.datasources.datasources[datasourceId]?.crossFilterModels ?? EMPTY_CROSS_FILTERS;
  const crossFilters = Object.values(crossFilterModels).filter(Boolean);
  if (!crossFilters.length) return widgetFilter;
  const conditions = [...(widgetFilter != null ? [widgetFilter] : []), ...crossFilters];
  if (conditions.length === 1) return conditions[0];
  return { filterType: 'join', type: 'AND', conditions };
};

export default datasourceSlice.reducer;
